import {
  ButtonV2 as Button,
  DropdownV3 as Dropdown,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import FormSection from './FormSection';
import FormTitle from './FormTitle';
import { useTrademarkVerificationControllers } from './hooks';
import { TrademarkVerificationFormValues } from './types';
import usePatentOfficeOptions from '../../hooks/usePatentOfficeOptions';
import { DefaultTrademarkVerificationFormValues } from './constants';

interface TrademarkVerificationFormProps {
  defaultValues?: TrademarkVerificationFormValues;
  onChange?: (data: TrademarkVerificationFormValues) => void;
  onBack?: () => void;
  onNext?: () => void;
}

const TrademarkVerificationForm: FunctionComponent<
  TrademarkVerificationFormProps
> = ({ defaultValues, onChange, onBack, onNext }) => {
  const patentOfficeOptions = usePatentOfficeOptions();
  const {
    control,
    formState: { errors, isValidating },
    reset,
    handleSubmit,
    watch,
  } = useForm<TrademarkVerificationFormValues>({
    defaultValues,
  });
  const {
    // Trademark Registration Number
    registrationId,
    setRegistrationId,
    validateRegistrationId,
    registrationIdError,
    // Trademark Wording
    trademarkText,
    setTrademarkText,
    validateTrademarkText,
    trademarkTextError,
    // Trademark Owner
    trademarkOwner,
    setTrademarkOwner,
    validateTrademarkOwner,
    trademarkOwnerError,
    // Trademark Office
    patentOffice,
    setPatentOffice,
    patentOfficeError,
  } = useTrademarkVerificationControllers(control);

  const [isSkipping, setIsSkipping] = useState(false);

  const handleValid = () => {
    onNext?.();
  };

  const handleSkip = () => {
    reset(DefaultTrademarkVerificationFormValues);
    setIsSkipping(true);
  };

  useEffect(() => {
    if (isSkipping) {
      onNext?.();
      setIsSkipping(false);
    }
  }, [isSkipping, onNext]);

  useEffect(() => {
    const { unsubscribe } = watch((data) => {
      onChange?.(data as TrademarkVerificationFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Flex sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}>
        <FormTitle
          title="Trademark Verification (Optional)"
          description="If you have a registered German trademark, provide it here. Otherwise, you can skip this and provide it later."
        />
        <FormSection>
          <TextInput
            required
            error={!!registrationIdError?.message}
            label="Trademark Registration Number"
            note={registrationIdError?.message}
            value={registrationId}
            onChange={setRegistrationId}
            onBlur={validateRegistrationId}
            data-testid="registrationId"
          />
          <TextInput
            required
            error={!!trademarkTextError?.message}
            label="Trademark Wording"
            note={trademarkTextError?.message}
            value={trademarkText}
            onChange={setTrademarkText}
            onBlur={validateTrademarkText}
            data-testid="trademarkText"
          />
          <TextInput
            error={!!trademarkOwnerError?.message}
            label="Trademark Owner"
            note={
              trademarkOwnerError?.message ||
              "*This is required if the Trademark Owner is different than the brand's legal name."
            }
            value={trademarkOwner}
            onChange={setTrademarkOwner}
            onBlur={validateTrademarkOwner}
            data-testid="trademarkOwner"
          />
          <Dropdown
            required
            error={!!patentOfficeError?.message}
            label="Trademark Office"
            note={patentOfficeError?.message}
            options={patentOfficeOptions}
            value={patentOffice}
            onChange={setPatentOffice}
            data-testid="patentOffice"
          />
        </FormSection>
        <Flex sx={{ justifyContent: 'flex-end', gap: 'm' }}>
          <Button
            shape="square"
            variant="outline"
            size="lg"
            color="secondary"
            onClick={onBack}
            data-testid="back"
          >
            Back
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            shape="square"
            variant="outline"
            size="lg"
            color="secondary"
            onClick={handleSkip}
            data-testid="skip"
          >
            Skip for Now
          </Button>
          <Button
            disabled={
              isValidating ||
              Object.values(errors).some((error) => !!error.message)
            }
            shape="square"
            type="submit"
            size="lg"
            color="secondary"
            data-testid="submit"
          >
            Next: Sender ID
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default TrademarkVerificationForm;
